/**
 * Where an NPC is, and whether it is keyed up.
 *
 * Two pure functions of game time. That is the whole design, and everything good about this
 * chapter follows from it:
 *
 * - Every client computes the same answer, so two players on the same band meet the same NPC.
 * - The validator can sweep a whole night without playing it, and prove an NPC is findable and
 *   never collides with a story signal.
 * - A save resumed a week later finds the NPC where the world says it should be, not wherever a
 *   random seed happened to land.
 *
 * Nothing here reads the wall clock or a random number generator.
 *
 * ## Why not a rotation
 *
 * Cycling through the stations in order would be deterministic too — and obviously mechanical. A
 * player would learn the loop in two nights, and an NPC you can predict is a scheduled broadcast
 * rather than someone moving around. So the station for each slot comes from hashing the NPC's id
 * with the slot number: a fixed, replayable permutation that does not read as a cycle. The same
 * trick as Chapter 2's band texture, for the same reason.
 */
import type { PackGate, PackRoaming } from '../content/schema.js';

const MS_PER_MINUTE = 60_000;

/**
 * A small integer hash. Deterministic across machines and runs — which is the entire requirement,
 * so it uses only integer operations rather than anything float-dependent.
 */
export function hashSlot(seed: string, slot: number): number {
  let hash = 2166136261 ^ slot;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    // FNV-1a's prime, applied with Math.imul so it stays a 32-bit integer multiply.
    hash = Math.imul(hash, 16777619);
  }
  // Mix the slot again at the end, so adjacent slots do not produce adjacent hashes.
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0x5bd1e995);
  hash ^= hash >>> 15;
  return hash >>> 0;
}

/** Which slot of the roaming schedule a moment falls in. */
export function slotAt(roaming: PackRoaming, gameMs: number): number {
  const dwellMs = Math.max(roaming.dwellMinutes, 1) * MS_PER_MINUTE;
  const offsetMs = (roaming.offsetMinutes ?? 0) * MS_PER_MINUTE;
  return Math.floor((gameMs - offsetMs) / dwellMs);
}

/** Which station the NPC is working at this moment. */
export function stationAt(npcId: string, roaming: PackRoaming, gameMs: number): number {
  const stations = roaming.stations;
  if (stations.length === 0) return Number.NaN;
  if (stations.length === 1) return stations[0]!;
  const slot = slotAt(roaming, gameMs);
  return stations[hashSlot(npcId, slot) % stations.length]!;
}

/**
 * Is the NPC keyed up right now, or listening?
 *
 * An NPC that transmitted continuously would be a carrier, not a person. The burst pattern is
 * anchored to the *slot*, not to the world, so arriving at a new station starts with a
 * transmission rather than dropping the player into the middle of a silence.
 */
export function isTransmittingAt(roaming: PackRoaming, gameMs: number): boolean {
  const transmitMinutes = roaming.transmitMinutes;
  if (transmitMinutes === undefined || transmitMinutes <= 0) return true;

  const cooldownMinutes = roaming.cooldownMinutes ?? 0;
  const cycleMinutes = transmitMinutes + cooldownMinutes;
  if (cycleMinutes <= 0) return true;

  const dwellMs = Math.max(roaming.dwellMinutes, 1) * MS_PER_MINUTE;
  const offsetMs = (roaming.offsetMinutes ?? 0) * MS_PER_MINUTE;
  const slot = slotAt(roaming, gameMs);
  const intoSlot = gameMs - offsetMs - slot * dwellMs;

  const intoCycle = intoSlot % (cycleMinutes * MS_PER_MINUTE);
  return intoCycle < transmitMinutes * MS_PER_MINUTE;
}

/** When the current slot ends — used to tell a player the NPC is about to move on. */
export function slotEndsAt(roaming: PackRoaming, gameMs: number): number {
  const dwellMs = Math.max(roaming.dwellMinutes, 1) * MS_PER_MINUTE;
  const offsetMs = (roaming.offsetMinutes ?? 0) * MS_PER_MINUTE;
  return offsetMs + (slotAt(roaming, gameMs) + 1) * dwellMs;
}

/**
 * Every station an NPC can ever occupy, including those its tasks pin it to.
 *
 * The validator uses this to check for collisions with story signals without having to sweep
 * every moment at fine resolution: if none of an NPC's possible stations is near a story signal's
 * frequency, no amount of sweeping will find one.
 */
export function possibleStations(
  roaming: PackRoaming | undefined,
  taskStations: number[],
): number[] {
  return [...new Set([...(roaming?.stations ?? []), ...taskStations])];
}

/** A task's claim on an NPC at a moment, as the matrix needs it. */
export interface RoamingOverride {
  stationKhz: number;
  dialogue?: string;
  requires?: PackGate;
}
